import { useBlocker } from "@tanstack/react-router";
import { useEffect } from "react";

import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";

interface UnsavedChangesGuardProps {
	blocking: boolean;
	title: string;
	description: string;
	stayLabel: string;
	leaveLabel: string;
}

export function UnsavedChangesGuard({
	blocking,
	title,
	description,
	stayLabel,
	leaveLabel,
}: UnsavedChangesGuardProps) {
	const blocker = useBlocker({
		shouldBlockFn: () => blocking,
		enableBeforeUnload: false,
		disabled: !blocking,
		withResolver: true,
	});

	useEffect(() => {
		if (!blocking) {
			return;
		}

		const handler = (event: BeforeUnloadEvent) => {
			event.preventDefault();
		};

		window.addEventListener("beforeunload", handler);

		return () => {
			window.removeEventListener("beforeunload", handler);
		};
	}, [blocking]);

	if (blocker.status !== "blocked") {
		return null;
	}

	return (
		<ConfirmationDialog
			tone="destructive"
			title={title}
			description={description}
			cancelLabel={stayLabel}
			confirmLabel={leaveLabel}
			pendingLabel={leaveLabel}
			isPending={false}
			onConfirm={blocker.proceed}
			open
			onOpenChange={(open) => !open && blocker.reset()}
			dialogComponent="unsaved-changes-dialog"
			cancelSlot="stay"
			confirmSlot="leave"
		/>
	);
}
